// TREPTELE SCĂRII ÎN 3D — din rampele citite de pe planșă sau, fără ele, din
// regula scării (`viewer/plan/scara.ts`), aceeași cu care editorul desenează o
// scară. Scara e doar afișare în modelul de structură: structuristul își pune
// pereții mari lângă ea, deci trebuie să se vadă unde e și cum urcă.
import { OCHI, scaraDin } from "../viewer/plan/scara";

/** O rampă, ca linie de urcare în metri (`model._nuclee_m`). */
export type RampaM = { de: [number, number]; la: [number, number]; contratrepte: number };

/** Scara unui nucleu: rampele ei și lățimea rampei. */
export type ScaraNucleu = {
  fel?: string | null;
  contratrepte?: number | null;
  lat_rampa_m?: number;
  treapta_m?: number;
  rampe: RampaM[];
};

/** O treaptă: o cutie centrată în (cx, cy), lungă `lung` pe direcția urcării
 *  (unghiul `unghi`, radiani), lată `lat`, de la `z0` în sus cu `h`. */
export type Treapta = {
  cx: number; cy: number; lung: number; lat: number; unghi: number; z0: number; h: number;
};

/** Podestul dintre două rampe: o placă cu fața de sus la `z`. */
export type Podest = {
  cx: number; cy: number; lung: number; lat: number; unghi: number; z: number; grosime: number;
};

/** Grosimea plăcii podestului. */
export const PODEST_M = 0.15;
/** Lățimea rampei când planșa n-o spune. */
export const LAT_RAMPA_IMPLICITA_M = 1.1;

/** TREPTELE ȘI PODESTELE unei scări, de la cota `zJos` până la `zJos + hEtaj`.
 *
 *  Contratreapta e înălțimea nivelului împărțită la toate contratreptele
 *  scării; fiecare rampă pune `contratrepte − 1` trepte vizibile (ultima urcă
 *  pe podest sau pe planșeu — regula din `viewer/plan/scara.ts`), iar podestul
 *  dintre două rampe stă la cota la care a ajuns prima. */
export function treptele(scara: ScaraNucleu, zJos: number, hEtaj: number):
  { trepte: Treapta[]; podeste: Podest[] } {
  const rampe = (scara.rampe ?? []).filter((r) => r.de && r.la);
  const total = rampe.reduce((s, r) => s + Math.max(1, r.contratrepte | 0), 0);
  if (!rampe.length || total <= 0 || !(hEtaj > 0)) return { trepte: [], podeste: [] };
  const hr = hEtaj / total;
  const lat = scara.lat_rampa_m && scara.lat_rampa_m > 0 ? scara.lat_rampa_m : LAT_RAMPA_IMPLICITA_M;
  const trepte: Treapta[] = [];
  const podeste: Podest[] = [];
  let zBaza = zJos;
  rampe.forEach((r, i) => {
    const n = Math.max(1, r.contratrepte | 0);
    const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
    const L = Math.hypot(dx, dy);
    const unghi = Math.atan2(dy, dx);
    const ux = L > 1e-9 ? dx / L : 1, uy = L > 1e-9 ? dy / L : 0;
    const vizibile = Math.max(1, n - 1);
    const adanc = L > 1e-9 ? L / vizibile : 0.28;
    for (let k = 0; k < vizibile; k++) {
      const d = (k + 0.5) * adanc;
      trepte.push({ cx: r.de[0] + ux * d, cy: r.de[1] + uy * d, lung: adanc, lat,
                    unghi, z0: zBaza + k * hr, h: hr });
    }
    const zSus = zBaza + n * hr;
    const urm = rampe[i + 1];
    if (urm) {
      // podestul: de la capătul rampei la începutul celeilalte, lat cât să
      // le cuprindă pe amândouă (la scara în U cele două rampe stau alături)
      const px = urm.de[0] - r.la[0], py = urm.de[1] - r.la[1];
      const lung = Math.hypot(px, py);
      // depărtarea dintre liniile celor două rampe, perpendicular pe prima
      const perp = Math.abs(-uy * (urm.de[0] - r.de[0]) + ux * (urm.de[1] - r.de[1]));
      const latPodest = perp > 1e-6 ? perp + lat : lat;
      // podestul stă în fața capătului rampei, pe direcția ei, nu pe linia
      // dintre capete (care la scara în U e transversală)
      const ex = L > 1e-9 ? ux : 1, ey = L > 1e-9 ? uy : 0;
      const adancPodest = Math.max(lung * Math.abs(ex * px + ey * py) / Math.max(lung, 1e-9), lat);
      const cx = (r.la[0] + urm.de[0]) / 2 + ex * adancPodest / 2;
      const cy = (r.la[1] + urm.de[1]) / 2 + ey * adancPodest / 2;
      podeste.push({ cx, cy, lung: adancPodest, lat: latPodest, unghi, z: zSus, grosime: PODEST_M });
    }
    zBaza = zSus;
  });
  return { trepte, podeste };
}

/** CEL MAI MARE DREPTUNGHI ÎNSCRIS în contur, cu laturile pe direcția
 *  (ux, uy): casa scării e rareori un dreptunghi — are puțul liftului, un
 *  palier sau un colț de zid în ea —, iar scara tipului stă în partea ei
 *  dreptunghiulară, nu peste tot conturul: treptele nu intră în pereți și
 *  nici în puțul liftului. Coordonatele candidate sunt ale vârfurilor în
 *  cadrul rotit; o celulă a grilei lor e înăuntru când centrul ei e înăuntru,
 *  iar un dreptunghi e înăuntru când toate celulele lui sunt.
 *  -> [sMin, sMax, tMin, tMax] în cadrul (s, t), sau null. */
export function dreptunghiulInscris(
  pts: [number, number][], ux: number, uy: number,
): [number, number, number, number] | null {
  const nx = -uy, ny = ux;
  const S = pts.map((p) => p[0] * ux + p[1] * uy);
  const T = pts.map((p) => p[0] * nx + p[1] * ny);
  const unice = (v: number[]) => {
    const o = [...v].sort((a, b) => a - b);
    return o.filter((x, i) => i === 0 || x - o[i - 1] > 1e-6);
  };
  const ss = unice(S), ts = unice(T);
  if (ss.length < 2 || ts.length < 2) return null;
  const inauntru = (s: number, t: number) => {
    let c = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const si = S[i], ti = T[i], sj = S[j], tj = T[j];
      if ((ti > t) !== (tj > t) && s < ((sj - si) * (t - ti)) / (tj - ti) + si) c = !c;
    }
    return c;
  };
  const ns = ss.length - 1, nt = ts.length - 1;
  // sume prefixate ale celulelor dinăuntru: P[i][j] = câte celule din
  // [0..i) × [0..j) sunt înăuntru
  const P: number[][] = Array.from({ length: ns + 1 }, () => new Array(nt + 1).fill(0));
  for (let i = 0; i < ns; i++) {
    for (let j = 0; j < nt; j++) {
      const e = inauntru((ss[i] + ss[i + 1]) / 2, (ts[j] + ts[j + 1]) / 2) ? 1 : 0;
      P[i + 1][j + 1] = P[i][j + 1] + P[i + 1][j] - P[i][j] + e;
    }
  }
  let best: [number, number, number, number] | null = null, arie = 0;
  for (let i0 = 0; i0 < ns; i0++) {
    for (let i1 = i0 + 1; i1 <= ns; i1++) {
      for (let j0 = 0; j0 < nt; j0++) {
        for (let j1 = j0 + 1; j1 <= nt; j1++) {
          const pline = P[i1][j1] - P[i0][j1] - P[i1][j0] + P[i0][j0];
          if (pline !== (i1 - i0) * (j1 - j0)) continue;
          const a = (ss[i1] - ss[i0]) * (ts[j1] - ts[j0]);
          if (a > arie) { arie = a; best = [ss[i0], ss[i1], ts[j0], ts[j1]]; }
        }
      }
    }
  }
  return best;
}

/** SCARA IMPLICITĂ a unui nucleu fără trepte pe planșă: cel mai mare
 *  dreptunghi înscris în contur, pe direcția laturii lui celei mai lungi
 *  (`dreptunghiulInscris`), împărțit după regula scării — în U când încap
 *  două rampe, dreaptă altfel. Rampele pleacă de la capătul dreptunghiului și
 *  se întorc pe lângă ochi. */
export function scaraImplicita(inel: [number, number][], hEtaj: number): ScaraNucleu | null {
  const pts = inel.filter((p) => p && p.length >= 2);
  if (pts.length < 3 || !(hEtaj > 0)) return null;
  // latura cea mai lungă dă direcția cutiei
  let best = 0, ang = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (l > best) { best = l; ang = Math.atan2(b[1] - a[1], b[0] - a[0]); }
  }
  const ux = Math.cos(ang), uy = Math.sin(ang);
  const nx = -uy, ny = ux;
  const cutie = dreptunghiulInscris(pts, ux, uy);
  if (!cutie) return null;
  const [sMin, sMax, tMin, tMax] = cutie;
  const lung = sMax - sMin, lat = tMax - tMin;
  if (!(lung > 0.3) || !(lat > 0.3)) return null;
  const tMij = (tMin + tMax) / 2;
  const la = (s: number, t: number): [number, number] =>
    [+(s * ux + t * nx).toFixed(3), +(s * uy + t * ny).toFixed(3)];
  if (lat < 2 * 0.6 + OCHI) {
    const sc = scaraDin("scara", lat, lung, hEtaj);
    return { fel: "scara", contratrepte: sc.contratrepte, lat_rampa_m: +lat.toFixed(3),
             treapta_m: +sc.treapta.toFixed(3),
             rampe: [{ de: la(sMin, tMij), la: la(sMax, tMij), contratrepte: sc.rampe[0] }] };
  }
  const sc = scaraDin("scaraU", lat, lung, hEtaj);
  const ofs = (sc.latRampa + OCHI) / 2;
  const sCap = sMin + sc.lungRampa;
  return { fel: "scaraU", contratrepte: sc.contratrepte, lat_rampa_m: +sc.latRampa.toFixed(3),
           treapta_m: +sc.treapta.toFixed(3),
           rampe: [{ de: la(sMin, tMij - ofs), la: la(sCap, tMij - ofs), contratrepte: sc.rampe[0] },
                   { de: la(sCap, tMij + ofs), la: la(sMin, tMij + ofs), contratrepte: sc.rampe[1] }] };
}
